import { useEffect, useRef, useState, type RefObject } from 'react'
import { useMotion } from '@/components/motion/motion'
import { createReededSurface, type ReededSurface } from '@/components/motion/reededGlass'

/**
 * Runs the reeded glass on a canvas that fills its parent panel. It animates only while visible and
 * not paused; with reduced motion it draws once and ignores the pointer. Without `ambient` motion it
 * stays still until the pointer stirs it. Returns false when WebGL is unavailable.
 */
export const useReededGlass = (canvasRef: RefObject<HTMLCanvasElement | null>, { ambient = true } = {}) => {
  const { paused, reduceMotion } = useMotion()
  const [supported, setSupported] = useState(true)
  const motion = useRef({ paused, reduceMotion })
  const setRunning = useRef<((paused: boolean) => void) | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const host = canvas?.parentElement
    if (!canvas || !host || typeof requestAnimationFrame === 'undefined') return

    let surface: ReededSurface | null = null
    let visible = false
    let isPaused = motion.current.paused
    let frame = 0
    let last = 0
    let resizeObserver: ResizeObserver | undefined
    let visibilityObserver: IntersectionObserver | undefined

    const shouldRun = () =>
      surface !== null && visible && !isPaused && !document.hidden && (ambient || surface.isStirred())
    const tick = (now: number) => {
      frame = 0
      if (!surface || !shouldRun()) return
      surface.frame(Math.min((now - last) / 1000, 0.05))
      last = now
      frame = requestAnimationFrame(tick)
    }
    const start = () => {
      if (frame || !shouldRun()) return
      last = performance.now()
      frame = requestAnimationFrame(tick)
    }
    const stop = () => {
      cancelAnimationFrame(frame)
      frame = 0
    }

    const onPointerMove = (event: PointerEvent) => {
      if (!surface || motion.current.reduceMotion) return
      const rect = canvas.getBoundingClientRect()
      surface.point((event.clientX - rect.left) / rect.width, 1 - (event.clientY - rect.top) / rect.height)
      start()
    }
    const onPointerLeave = () => surface?.leave()
    const onVisibilityChange = () => (document.hidden ? stop() : start())

    // Set up on the next frame, once the panel has its size.
    const setup = requestAnimationFrame(() => {
      surface = createReededSurface(canvas)
      if (!surface) {
        setSupported(false)
        return
      }
      resizeObserver = new ResizeObserver(() => surface?.resize())
      resizeObserver.observe(canvas)
      visibilityObserver = new IntersectionObserver(
        ([entry]) => {
          visible = entry.isIntersecting
          if (visible) start()
          else stop()
        },
        { rootMargin: '80px' },
      )
      visibilityObserver.observe(canvas)
      host.addEventListener('pointermove', onPointerMove)
      host.addEventListener('pointerleave', onPointerLeave)
      surface.resize()
    })
    document.addEventListener('visibilitychange', onVisibilityChange)

    setRunning.current = (nextPaused) => {
      isPaused = nextPaused
      if (isPaused) stop()
      else start()
    }

    return () => {
      cancelAnimationFrame(setup)
      stop()
      resizeObserver?.disconnect()
      visibilityObserver?.disconnect()
      host.removeEventListener('pointermove', onPointerMove)
      host.removeEventListener('pointerleave', onPointerLeave)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      surface?.dispose()
      setRunning.current = null
    }
  }, [canvasRef, ambient])

  useEffect(() => {
    motion.current = { paused, reduceMotion }
    setRunning.current?.(paused)
  }, [paused, reduceMotion])

  return supported
}
